/*
 * Hash router — hand-rolled, ~50 LOC of logic.
 *
 * Why hash routing (and not history API): keeps deployment requirements
 * dead-simple (no server rewrite rules), and the app is single-user with
 * no SEO needs. Worth revisiting only if we add public sharing.
 *
 * Route IDs are the part after `#/`. Empty hash resolves to the home
 * route (empty string). Unknown routes fall back to the registered
 * `not-found` screen, never a blank page.
 */

export type ScreenRenderer = (
  mount: HTMLElement,
  params?: Record<string, string>,
) => void | Promise<void>;

const screens = new Map<string, ScreenRenderer>();
let activeMount: HTMLElement | null = null;
let currentRouteId: string | null = null;
let currentParamsKey: string | null = null;
let navigationSeq = 0;

const FALLBACK_ROUTE = 'not-found';
const BASE_TRANSITION_MS = 240; // mirrors --motion-screen; jsdom doesn't fire transitionend reliably so we use a timer.

function transitionMs(): number {
  // Respect the OS-level reduced-motion setting. The CSS transitions in
  // base.css also honor it, but the JS timer is what makes the user wait
  // for the swap, so it has to short-circuit too.
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : BASE_TRANSITION_MS;
}

export function registerScreen(routeId: string, render: ScreenRenderer): void {
  screens.set(routeId, render);
}

export function startRouter(mount: HTMLElement): void {
  activeMount = mount;
  window.addEventListener('hashchange', handleRouteChange);
  void handleRouteChange();
}

export function navigate(routeId: string): void {
  const target = `#/${routeId}`;
  if (window.location.hash === target) {
    // Same hash → no hashchange event will fire. Re-render explicitly.
    void handleRouteChange();
    return;
  }
  window.location.hash = target;
}

function parseRoute(): { routeId: string; params: Record<string, string> } {
  const raw = window.location.hash.replace(/^#\/?/, '');
  // Future-proof: split on `?` for query params. We don't use them yet but
  // wiring it in now means screen renderers can opt in without a router change.
  const [pathPart = '', queryPart = ''] = raw.split('?');
  const params: Record<string, string> = {};
  if (queryPart) {
    for (const pair of queryPart.split('&')) {
      const [k, v] = pair.split('=');
      if (k) params[decodeURIComponent(k)] = decodeURIComponent(v ?? '');
    }
  }
  return { routeId: pathPart, params };
}

function resolveRenderer(routeId: string): { id: string; render: ScreenRenderer } {
  const direct = screens.get(routeId);
  if (direct) return { id: routeId, render: direct };

  const fallback = screens.get(FALLBACK_ROUTE);
  if (fallback) return { id: FALLBACK_ROUTE, render: fallback };

  // Last-ditch inline fallback. Should never hit this in practice because
  // main.ts registers a `not-found` screen at boot.
  return {
    id: FALLBACK_ROUTE,
    render: (mount) => {
      mount.replaceChildren();
      const msg = document.createElement('p');
      msg.textContent = 'Route not found.';
      mount.appendChild(msg);
    },
  };
}

async function handleRouteChange(): Promise<void> {
  if (!activeMount) return;
  const mount = activeMount;
  const seq = ++navigationSeq;
  const { routeId, params } = parseRoute();
  const { id, render } = resolveRenderer(routeId);
  const paramsKey = JSON.stringify(params);

  // Avoid double-rendering when both the resolved screen AND its params are
  // unchanged (e.g. unknown-route → unknown-route both map to `not-found`).
  // Params are compared so `#/x?a=1` → `#/x?a=2` still re-renders.
  if (
    currentRouteId === id &&
    currentParamsKey === paramsKey &&
    mount.childElementCount > 0
  ) {
    return;
  }
  currentRouteId = id;
  currentParamsKey = paramsKey;

  await playLeave(mount);
  if (seq !== navigationSeq) return; // superseded by a newer navigation
  mount.replaceChildren();
  await Promise.resolve(render(mount, params));
  if (seq !== navigationSeq) return; // superseded during render
  playEnter(mount);
}

function playLeave(mount: HTMLElement): Promise<void> {
  // Skip the fade on first paint — there's nothing to fade out from.
  if (mount.childElementCount === 0) return Promise.resolve();
  mount.classList.remove('screen--entering');
  mount.classList.add('screen--leaving');
  return new Promise((resolve) => {
    window.setTimeout(() => {
      mount.classList.remove('screen--leaving');
      resolve();
    }, transitionMs());
  });
}

function playEnter(mount: HTMLElement): void {
  mount.classList.add('screen--entering');
  window.setTimeout(() => {
    mount.classList.remove('screen--entering');
  }, transitionMs());
}

/**
 * Test-only reset. Not part of the public API contract but exported so
 * vitest suites can wipe state between cases without reloading the module.
 */
export function __resetRouterForTests(): void {
  screens.clear();
  activeMount = null;
  currentRouteId = null;
  currentParamsKey = null;
  navigationSeq = 0;
  window.removeEventListener('hashchange', handleRouteChange);
}
